import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// Shared-rig contract (see the framing addendum). 'forbidden': a field at
// dusk, lit by nothing in the scene.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'reed',
  family: 'organic',
  danceability: 0.28,
  energyLevel: 0.22,
  bpmMin: 40,
  bpmMax: 140,
  movement: 0.35,
  tonality: 0.88,
  prefers: { dynamics: [0.40, 1.0], percussiveness: [0.0, 0.50] },
  enabled: true
};

// Reed: reeds bending in wind. resonance.js's oscillator, transposed to a
// plant.
//
// WHAT MAKES IT A REED AND NOT A STRING. Both are damped oscillators, and that
// is where the resemblance ends. A string is stiff and light: it vibrates fast
// about its rest position and dies quickly, and the eye reads that as a
// shimmer. A stem is slack and heavy: it BENDS, holds the bend, and takes its
// time coming back. Same equation, an order of magnitude apart in both
// constants - so none of resonance.js's numbers are reused here. Dropped in as
// they are they would produce a vibration, which is precisely the wrong thing.
//
// THE OTHER DIFFERENCE IS THE EXCITATION. resonance.js plucks: an impulse, at
// an instant, on one string. Wind is not an impulse. It is a continuous force
// that ARRIVES SOMEWHERE and travels, so the stems it has already passed are
// returning while the ones ahead have not yet moved. That lag between
// neighbours is the whole effect; without it thirty-four stems moving together
// read as one object hinging, not as a field in wind.
//
// Like resonance.js: no beat pulse, no cuts. Everything arrives through
// director.bands.

// --- the stems --------------------------------------------------------------
// Slow. A quarter of a hertz at the tall end is a stem that takes four seconds
// to complete one swing - resonance.js's strings run ten times faster.
const FREQ_MIN_HZ = 0.25;
const FREQ_MAX_HZ = 1.10;
const DAMPING_MIN = 1.6;
const DAMPING_MAX = 2.4;
const HEIGHT_MIN = 2.4;
const HEIGHT_MAX = 5.2;
// Tall stems are slack and heavily damped: they bend furthest and take longest
// to come back. Short ones are stiff. One correlation, and it is what gives
// the field its variety at no cost at all.
//
// Damping is written as a RATIO of critical, not as the raw coefficient the
// brief quoted. Those figures (1.6..2.4 against omega) leave the short stems
// at a damping ratio of 0.12 - nine visible cycles of ringing before they
// settle, which is the shimmer this file exists to avoid. Expressing it as a
// ratio keeps the intent (heavier damping on the tall stems, all of them
// returning without oscillating) and holds it at every frequency.
const ZETA_TALL = 0.85;
const ZETA_SHORT = 0.62;

// Fixed step, substepped - the same reason sand.js does it. The return to rest
// is an exponential in dt; integrate it with whatever the frame happened to
// take and a 30fps machine settles at a visibly different rate from a 60fps
// one. Criterion 6 is that a gust crosses at the same speed either way.
const FIXED_DT = 1 / 60;
const MAX_SUBSTEPS = 3;

// --- the curve --------------------------------------------------------------
// A stem does not pivot. The bend accumulates along it, so the base barely
// moves and the tip moves a lot. An exponent above 1 is what concentrates the
// curvature toward the top; at 1.0 the whole thing swings like a needle.
const CURVE_EXPONENT = 1.6;
// Steps in the shader's own arc integration. The curve's POSITION is an
// integral of its tangent, and there is no closed form for it - but its
// tangent is exact and free, which is what the ear orientation and the ribbon
// normal actually need.
const INTEG_STEPS = 10;
const WIDTH_BASE_PX = 4.0;
const WIDTH_TIP_PX = 1.5;
const EAR_PX_MIN = 20.0;
const EAR_PX_MAX = 40.0;
const EAR_ASPECT = 0.30; // width as a fraction of length

// --- the wind ---------------------------------------------------------------
const GUST_MAX = 4;
const GUST_CROSS_MIN_S = 3.0;
const GUST_CROSS_MAX_S = 6.0;
const GUST_RISE_THRESHOLD = 0.045;
const GUST_MIN_SPACING_S = 0.45;
// bands.low widens and slows a gust and leans on it harder; bands.high makes it
// narrow, quick and light - a shiver through the tips rather than a push.
const GUST_SIGMA_LOW = 4.2;
const GUST_SIGMA_HIGH = 1.1;
const GUST_FORCE_LOW = 26.0;
const GUST_FORCE_HIGH = 9.0;
// How far past the frame edge a gust is born and dies, so it never appears or
// vanishes on screen.
const GUST_MARGIN = 3.0;
// A far row feels a gust slightly later - the front it belongs to is a plane
// travelling through the field, not a line.
const ROW_GUST_LAG_S = 0.22;

// Nothing is ever perfectly still. A slow LFO under everything, strengthened
// toward the middle of the track.
const BACKGROUND_WIND_PERIOD_S = 11.0;
const BACKGROUND_WIND_FORCE = 1.35;
const BACKGROUND_WIND_PROGRESS_MIN = 0.45;

const DYNAMICS_MIN = 0.6;
const DYNAMICS_MAX = 1.4;

// --- no-analysis fallback ---------------------------------------------------
const FALLBACK_GUST_MIN_S = 4.0;
const FALLBACK_GUST_MAX_S = 9.0;

// --- layout -----------------------------------------------------------------
const CAMERA_POS = [0, 2.2, 8.5];
const CAMERA_LOOK = [0, 2.6, 0];
// The ground line and the stem scale are DERIVED from the frustum, not written
// down. The brief's own numbers are mutually impossible: a camera at z=8.5
// with this 68-degree field makes the visible frame 11.5 units tall, and a
// 5.2-unit stem planted at its foot reaches 45% of the way up - nowhere near
// the "tiges depassent le cadre en haut" the same paragraph asks for, and it
// leaves the top third of the picture empty.
//
// So HEIGHT_MIN/HEIGHT_MAX are kept as a RATIO (a tall stem is 2.17x a short
// one, exactly as specified) and scaled to the frame at init. The physics is
// untouched by this: what the oscillator integrates is an ANGLE, and stiffness
// and damping come from the frequency, not from the length.
const GROUND_MARGIN_FRAC = 0.02;   // of frame height, below the visible bottom edge
const TALLEST_OVERFLOW = 1.06;     // tallest stem, as a fraction of frame height
const FIELD_WIDTH_FRAC = 0.52;     // half-width of the planting, as a fraction of frame width
// Rows, front to back. weight is how many of the tier's stems land in each.
const ROWS = [
  { z: 0.0, weight: 14, scale: 1.00, fade: 0.00 },
  { z: -3.0, weight: 12, scale: 0.82, fade: 0.45 },
  { z: -6.5, weight: 8, scale: 0.64, fade: 0.80 }
];


// --- colour -----------------------------------------------------------------
// Near-monochrome, deliberately: resonance.js spreads the palette across its
// strings, and doing that here would turn a field into a bouquet.
const STEM_SATURATION = 0.22;
const STEM_LIGHT_BASE = 0.13;
const STEM_LIGHT_TIP = 0.34;
const EAR_DESATURATE = 0.60;
const EAR_LIGHT = 0.52;
const BACK_TOP = new THREE.Color(0x07080b);
const BACK_BOTTOM = new THREE.Color(0x14161c);
const BACK_GRAIN = 0.010;
const FALLBACK_DOMINANT = 0x3d4a3a;
const FALLBACK_VIVID = 0xd8c58a;
const PEAK_LUMINANCE = 0.92;

const TIER_LAYOUT = {
  // points along a stem: 7 to 12 made a bent stem a chain of straight
  // segments with visible corners; 24 to 40 is a curve
  0: { stems: 18, rows: 2, points: 24 },
  1: { stems: 26, rows: 3, points: 32 },
  2: { stems: 34, rows: 3, points: 40 }
};
const DEFAULT_TIER = 1;

const SAFE_ZONE_FLOOR = 0.35;

function clamp(x, lo, hi) { return Math.max(lo, Math.min(hi, x)); }
function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// Own local copy with its own floor - the shared chunk bakes in 0.32. Same
// "own chunk, own floor" pattern sumi.js (0.40) and resonance.js (0.30) use.
const safeZoneChunk = `
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  float safeZoneAttenuation(vec4 clipPos) {
    vec2 ndc = clipPos.xy / max(clipPos.w, 0.0001);
    vec2 s = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_ZONE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
`;

// THE STEM, shared verbatim by the ribbon and the ear so the two can never
// disagree about where the tip is or which way it points.
function stemChunk(stemCount) {
  return `
  uniform float uAngle[${stemCount}];

  // Local bend at arc position t. The exponent is the whole difference between
  // a stem and a needle.
  float bendAt(float t, float angle) {
    return angle * pow(max(t, 0.0), ${CURVE_EXPONENT.toFixed(2)});
  }

  // Exact, and free: the tangent needs no integration.
  vec2 stemTangent(float t, float angle) {
    float a = bendAt(t, angle);
    return vec2(sin(a), cos(a));
  }

  // Position is the integral of that tangent from the base to t. Midpoint
  // rule, ${INTEG_STEPS} steps - the curve has no closed form, and this is
  // where the "computed in the vertex shader, one float per stem uploaded"
  // requirement is actually met.
  vec2 stemOffset(float t, float angle) {
    vec2 p = vec2(0.0);
    float ds = t / ${INTEG_STEPS.toFixed(1)};
    for (int i = 0; i < ${INTEG_STEPS}; i++) {
      float s = (float(i) + 0.5) * ds;
      p += stemTangent(s, angle);
    }
    return p * ds;
  }
`;
}

function stemVertexShader(stemCount, points) {
  return `
  precision mediump float;

  attribute float aSeg;      // which segment of the strip
  attribute float aStem;     // index into uAngle
  attribute vec3 aBase;      // where it is planted
  attribute float aHeight;
  attribute float aFade;     // 0 near row .. 1 far row

  uniform float uPixelScale;

  varying float vT;
  varying float vFade;
  varying float vAcross;
  varying float vSafeAtt;

  ${safeZoneChunk}
  ${stemChunk(stemCount)}

  void main() {
    float angle = uAngle[int(aStem)];
    float t = (aSeg + position.y) / ${(points - 1).toFixed(1)};

    vec2 off = stemOffset(t, angle) * aHeight;
    vec3 world = aBase + vec3(off, 0.0);

    vec2 dir = stemTangent(t, angle);
    vec2 perp = vec2(-dir.y, dir.x);

    // Pixels -> world at THIS stem's depth, so a far row's ribbon is the same
    // thickness on screen as a near one's. Every row sits in its own plane
    // parallel to the camera, so a world-space offset is exact here.
    vec4 mv = modelViewMatrix * vec4(world, 1.0);
    float worldPerPx = max(-mv.z, 0.001) / uPixelScale;
    float halfPx = mix(${(WIDTH_BASE_PX / 2).toFixed(2)}, ${(WIDTH_TIP_PX / 2).toFixed(2)}, t);
    world += vec3(perp * (position.x * halfPx * worldPerPx), 0.0);

    vT = t;
    vFade = aFade;
    vAcross = position.x;

    vec4 clip = projectionMatrix * modelViewMatrix * vec4(world, 1.0);
    gl_Position = clip;
    vSafeAtt = safeZoneAttenuation(clip);
  }
`;
}

const stemFragmentShader = `
  precision mediump float;

  uniform vec3 uStemBase;
  uniform vec3 uStemTip;
  uniform vec3 uBackColor;

  varying float vT;
  varying float vFade;
  varying float vAcross;
  varying float vSafeAtt;

  void main() {
    // Lighter toward the tip - a stem catches what light there is on its top
    // half, and it also separates the tips from the mass below them.
    vec3 col = mix(uStemBase, uStemTip, vT);
    // Atmospheric perspective: the far row is pulled toward the background it
    // sits against, which is what puts it BEHIND rather than merely smaller.
    col = mix(col, uBackColor, vFade * 0.72);
    float across = 1.0 - smoothstep(0.45, 1.0, abs(vAcross));
    gl_FragColor = vec4(col * across * vSafeAtt * ${PEAK_LUMINANCE.toFixed(2)}, across * vSafeAtt);
  }
`;

function earVertexShader(stemCount) {
  return `
  precision mediump float;

  attribute float aStem;
  attribute vec3 aBase;
  attribute float aHeight;
  attribute float aFade;
  attribute float aEarPx;

  uniform float uPixelScale;

  varying vec2 vQuad;
  varying float vFade;
  varying float vSafeAtt;

  ${safeZoneChunk}
  ${stemChunk(stemCount)}

  void main() {
    float angle = uAngle[int(aStem)];

    // The tip, and the direction the stem is pointing when it gets there.
    vec2 off = stemOffset(1.0, angle) * aHeight;
    vec3 world = aBase + vec3(off, 0.0);
    vec2 dir = stemTangent(1.0, angle);
    vec2 perp = vec2(-dir.y, dir.x);

    vec4 mv = modelViewMatrix * vec4(world, 1.0);
    float worldPerPx = max(-mv.z, 0.001) / uPixelScale;

    // Built ON the tangent, so the ear lies along the stem instead of standing
    // upright while the stem bends away underneath it - which is what makes a
    // bend legible at all.
    float halfLen = aEarPx * 0.5 * worldPerPx;
    float halfWid = aEarPx * ${EAR_ASPECT.toFixed(2)} * 0.5 * worldPerPx;
    world += vec3(dir * (position.y * halfLen) + perp * (position.x * halfWid), 0.0);

    vQuad = position.xy;
    vFade = aFade;
    vec4 clip = projectionMatrix * modelViewMatrix * vec4(world, 1.0);
    gl_Position = clip;
    vSafeAtt = safeZoneAttenuation(clip);
  }
`;
}

const earFragmentShader = `
  precision mediump float;

  uniform vec3 uEarColor;
  uniform vec3 uBackColor;

  varying vec2 vQuad;
  varying float vFade;
  varying float vSafeAtt;

  void main() {
    // A lozenge, not a rectangle: tapered at both ends.
    float taper = 1.0 - abs(vQuad.y);
    float across = 1.0 - smoothstep(0.0, 1.0, abs(vQuad.x) / max(taper, 0.001));
    float a = across * smoothstep(0.0, 0.25, taper);
    vec3 col = mix(uEarColor, uBackColor, vFade * 0.72);
    gl_FragColor = vec4(col * a * vSafeAtt * ${PEAK_LUMINANCE.toFixed(2)}, a * vSafeAtt);
  }
`;

const backVertexShader = `
  precision mediump float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

const backFragmentShader = `
  precision mediump float;
  uniform vec3 uTop;
  uniform vec3 uBottom;
  varying vec2 vUv;
  void main() {
    // Lighter toward the ground, which still holds a little light after the
    // sky has gone.
    vec3 col = mix(uTop, uBottom, pow(1.0 - vUv.y, 1.35));
    // Ordered dither before the 8-bit quantisation. A gradient this shallow
    // holds each output level for tens of pixels, and a plateau that wide IS a
    // visible band - see constellation.js, which found this the hard way.
    float d = (fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453) - 0.5) / 255.0;
    col += d + (fract(sin(dot(floor(gl_FragCoord.xy), vec2(93.1, 47.7))) * 24634.6345) - 0.5)
               * ${BACK_GRAIN.toFixed(4)};
    gl_FragColor = vec4(max(col, vec3(0.0)), 1.0);
  }
`;

export class ReedVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER_LAYOUT[tier] ? tier : DEFAULT_TIER;
    this._forceGust = urlParam('reedgust') === '1';
    const w = parseFloat(urlParam('reedwind'));
    this._forcedWind = Number.isFinite(w) ? w : null;
    this._info = urlParam('reedinfo') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    camera.position.set(CAMERA_POS[0], CAMERA_POS[1], CAMERA_POS[2]);
    camera.lookAt(CAMERA_LOOK[0], CAMERA_LOOK[1], CAMERA_LOOK[2]);

    // Frustum at the front row, which is what the framing is judged against.
    const fovRad = THREE.MathUtils.degToRad(camera.fov);
    const visH = 2 * Math.abs(CAMERA_POS[2]) * Math.tan(fovRad / 2);
    const visW = visH * camera.aspect;
    this._groundY = CAMERA_LOOK[1] - visH / 2 - visH * GROUND_MARGIN_FRAC;
    this._heightScale = (visH * TALLEST_OVERFLOW) / HEIGHT_MAX;
    this._fieldHalfWidth = visW * FIELD_WIDTH_FRAC;

    const layout = TIER_LAYOUT[this._tier];
    this._stemCount = layout.stems;
    this._points = layout.points;
    const rows = ROWS.slice(0, layout.rows);

    // Per-stem state and constants, allocated once.
    const n = this._stemCount;
    this._angle = new Float32Array(n);
    this._vel = new Float32Array(n);
    this._stiff = new Float32Array(n);
    this._damp = new Float32Array(n);
    this._height = new Float32Array(n);
    this._x = new Float32Array(n);
    this._rowIndex = new Int32Array(n);
    // The uniform array the shaders read - one float per stem, the only thing
    // that changes per frame.
    this._angleUniform = new Array(n).fill(0);

    const weightTotal = rows.reduce((s, r) => s + r.weight, 0);
    const base = new Float32Array(n * 3);
    const heights = new Float32Array(n);
    const fades = new Float32Array(n);
    const earPx = new Float32Array(n);

    let k = 0;
    for (let r = 0; r < rows.length; r++) {
      const row = rows[r];
      const count = r === rows.length - 1
        ? n - k
        : Math.max(1, Math.round((row.weight / weightTotal) * n));
      for (let j = 0; j < count && k < n; j++, k++) {
        // Irregular by construction: an even spacing plus jitter still reads
        // as a row of fence posts, so the slot itself is drawn unevenly.
        const u = (j + 0.5) / count + (Math.random() * 2 - 1) * (0.85 / count);
        const x = (clamp01(u) * 2 - 1) * this._fieldHalfWidth;
        const tall = Math.random();
        const h = lerp(HEIGHT_MIN, HEIGHT_MAX, tall) * row.scale * this._heightScale;

        this._x[k] = x;
        this._rowIndex[k] = r;
        this._height[k] = h;
        // Tall = slack and heavily damped; short = stiff. One correlation,
        // and the reason the field never moves as one piece.
        const freq = lerp(FREQ_MAX_HZ, FREQ_MIN_HZ, tall);
        const omega = 2 * Math.PI * freq;
        this._stiff[k] = omega * omega;
        this._damp[k] = 2 * lerp(ZETA_SHORT, ZETA_TALL, tall) * omega;

        base[k * 3] = x;
        base[k * 3 + 1] = this._groundY;
        base[k * 3 + 2] = row.z;
        heights[k] = h;
        fades[k] = row.fade;
        earPx[k] = lerp(EAR_PX_MIN, EAR_PX_MAX, Math.random()) * row.scale;
      }
    }

    // Gust ring buffer, pre-allocated.
    this._gustX = new Float32Array(GUST_MAX);
    this._gustAmp = new Float32Array(GUST_MAX);
    this._gustSigma = new Float32Array(GUST_MAX);
    this._gustSpeed = new Float32Array(GUST_MAX);
    this._gustAlive = new Uint8Array(GUST_MAX);
    this._gustWrite = 0;

    this._time = 0;
    this._accumulator = 0;
    this._prevMid = 0;
    this._lastGustAt = -1e6;
    this._nextFallbackGust = 0;
    this._windScale = 1;
    this._backgroundWind = 0;
    this._lastPaletteRef = undefined;
    this._scratchColor = new THREE.Color();
    this._scratchHsl = { h: 0, s: 0, l: 0 };

    this._buildBackground();
    this._buildStems(base, heights, fades);
    this._buildEars(base, heights, fades, earPx);
    this._applyPalette(null);
  }

  _sharedUniforms() {
    return Object.assign({}, safeZoneUniforms, {
      uAngle: { value: this._angleUniform },
      uPixelScale: { value: 1 },
      uStemBase: { value: new THREE.Color(0x1a1f18) },
      uStemTip: { value: new THREE.Color(0x3a4436) },
      uEarColor: { value: new THREE.Color(FALLBACK_VIVID) },
      uBackColor: { value: BACK_TOP.clone() }
    });
  }

  _buildBackground() {
    this._backGeometry = new THREE.PlaneGeometry(1, 1);
    this._backUniforms = {
      uTop: { value: BACK_TOP.clone() },
      uBottom: { value: BACK_BOTTOM.clone() }
    };
    this._backMaterial = new THREE.ShaderMaterial({
      uniforms: this._backUniforms,
      vertexShader: backVertexShader,
      fragmentShader: backFragmentShader,
      depthTest: false,
      depthWrite: false
    });
    this._back = new THREE.Mesh(this._backGeometry, this._backMaterial);
    this._back.frustumCulled = false;
    this._back.renderOrder = -100;
    this._scene.add(this._back);
  }

  // ONE draw call: every segment of every stem, as instanced quads.
  _buildStems(base, heights, fades) {
    const segs = this._points - 1;
    const total = this._stemCount * segs;
    const quad = new Float32Array([-1, 0, 0, 1, 0, 0, 1, 1, 0, -1, 1, 0]);
    const g = new THREE.InstancedBufferGeometry();
    g.setIndex([0, 1, 2, 0, 2, 3]);
    g.setAttribute('position', new THREE.BufferAttribute(quad, 3));

    const aSeg = new Float32Array(total);
    const aStem = new Float32Array(total);
    const aBase = new Float32Array(total * 3);
    const aHeight = new Float32Array(total);
    const aFade = new Float32Array(total);
    for (let s = 0; s < this._stemCount; s++) {
      for (let e = 0; e < segs; e++) {
        const i = s * segs + e;
        aSeg[i] = e;
        aStem[i] = s;
        aBase[i * 3] = base[s * 3];
        aBase[i * 3 + 1] = base[s * 3 + 1];
        aBase[i * 3 + 2] = base[s * 3 + 2];
        aHeight[i] = heights[s];
        aFade[i] = fades[s];
      }
    }
    g.setAttribute('aSeg', new THREE.InstancedBufferAttribute(aSeg, 1));
    g.setAttribute('aStem', new THREE.InstancedBufferAttribute(aStem, 1));
    g.setAttribute('aBase', new THREE.InstancedBufferAttribute(aBase, 3));
    g.setAttribute('aHeight', new THREE.InstancedBufferAttribute(aHeight, 1));
    g.setAttribute('aFade', new THREE.InstancedBufferAttribute(aFade, 1));
    g.instanceCount = total;
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 60);

    this._stemUniforms = this._sharedUniforms();
    this._stemMaterial = new THREE.ShaderMaterial({
      uniforms: this._stemUniforms,
      vertexShader: stemVertexShader(this._stemCount, this._points),
      fragmentShader: stemFragmentShader,
      transparent: true,
      depthTest: true,
      depthWrite: false,
      side: THREE.DoubleSide
    });
    this._stemGeometry = g;
    this._stemMesh = new THREE.Mesh(g, this._stemMaterial);
    this._stemMesh.frustumCulled = false;
    this._stemMesh.renderOrder = 1;
    this._scene.add(this._stemMesh);
  }

  // ONE draw call: every ear.
  _buildEars(base, heights, fades, earPx) {
    const n = this._stemCount;
    const quad = new Float32Array([-1, -1, 0, 1, -1, 0, 1, 1, 0, -1, 1, 0]);
    const g = new THREE.InstancedBufferGeometry();
    g.setIndex([0, 1, 2, 0, 2, 3]);
    g.setAttribute('position', new THREE.BufferAttribute(quad, 3));
    const aStem = new Float32Array(n);
    for (let i = 0; i < n; i++) aStem[i] = i;
    g.setAttribute('aStem', new THREE.InstancedBufferAttribute(aStem, 1));
    g.setAttribute('aBase', new THREE.InstancedBufferAttribute(base.slice(), 3));
    g.setAttribute('aHeight', new THREE.InstancedBufferAttribute(heights.slice(), 1));
    g.setAttribute('aFade', new THREE.InstancedBufferAttribute(fades.slice(), 1));
    g.setAttribute('aEarPx', new THREE.InstancedBufferAttribute(earPx.slice(), 1));
    g.instanceCount = n;
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 60);

    // Shares the stem's uniform holders, so uAngle is written once and both
    // shaders see the same value - the ear cannot drift off its own stem.
    this._earUniforms = Object.assign({}, this._stemUniforms);
    this._earMaterial = new THREE.ShaderMaterial({
      uniforms: this._earUniforms,
      vertexShader: earVertexShader(this._stemCount),
      fragmentShader: earFragmentShader,
      transparent: true,
      depthTest: true,
      depthWrite: false,
      side: THREE.DoubleSide
    });
    this._earGeometry = g;
    this._earMesh = new THREE.Mesh(g, this._earMaterial);
    this._earMesh.frustumCulled = false;
    this._earMesh.renderOrder = 2;
    this._scene.add(this._earMesh);
  }

  // --- wind -----------------------------------------------------------------

  _spawnGust(amount, low, high) {
    const slot = this._gustWrite;
    // Character from the bands: low leans on the field, high shivers the tips.
    const mixHigh = clamp01(high / Math.max(low + high, 0.001));
    const sigma = lerp(GUST_SIGMA_LOW, GUST_SIGMA_HIGH, mixHigh);
    const force = lerp(GUST_FORCE_LOW, GUST_FORCE_HIGH, mixHigh);
    const cross = lerp(GUST_CROSS_MAX_S, GUST_CROSS_MIN_S, mixHigh);
    const span = (this._fieldHalfWidth + GUST_MARGIN) * 2;

    this._gustX[slot] = -(this._fieldHalfWidth + GUST_MARGIN);
    this._gustAmp[slot] = force * clamp01(amount) * this._windScale;
    this._gustSigma[slot] = sigma;
    this._gustSpeed[slot] = span / cross;
    this._gustAlive[slot] = 1;
    this._gustWrite = (this._gustWrite + 1) % GUST_MAX;
    this._lastGustAt = this._time;
  }

  // Force on one stem: every live gust, plus the background LFO. A gust is a
  // travelling gaussian, so a stem feels it arrive, peak and pass.
  _windAt(x, rowIndex) {
    let f = this._backgroundWind;
    for (let g = 0; g < GUST_MAX; g++) {
      if (!this._gustAlive[g]) continue;
      // The far rows are behind the front, so they feel it later.
      const lag = rowIndex * ROW_GUST_LAG_S * this._gustSpeed[g];
      const d = (x - (this._gustX[g] - lag)) / this._gustSigma[g];
      f += this._gustAmp[g] * Math.exp(-d * d);
    }
    return f;
  }

  _step(dt) {
    const span = (this._fieldHalfWidth + GUST_MARGIN) * 2;
    for (let g = 0; g < GUST_MAX; g++) {
      if (!this._gustAlive[g]) continue;
      this._gustX[g] += this._gustSpeed[g] * dt;
      if (this._gustX[g] > this._fieldHalfWidth + GUST_MARGIN + span * 0.15) this._gustAlive[g] = 0;
    }
    for (let i = 0; i < this._stemCount; i++) {
      const force = this._windAt(this._x[i], this._rowIndex[i]);
      const accel = -this._stiff[i] * this._angle[i] - this._damp[i] * this._vel[i] + force;
      this._vel[i] += accel * dt;
      this._angle[i] += this._vel[i] * dt;
    }
  }

  _applyPalette(palette) {
    const dominant = (palette && palette.dominantColors && palette.dominantColors.length)
      ? palette.dominantColors[0] : FALLBACK_DOMINANT;
    const vivid = (palette && palette.vividColors && palette.vividColors.length)
      ? palette.vividColors[0] : FALLBACK_VIVID;
    const hsl = this._scratchHsl;

    this._scratchColor.set(dominant).getHSL(hsl);
    this._stemUniforms.uStemBase.value.setHSL(hsl.h, STEM_SATURATION, STEM_LIGHT_BASE);
    this._stemUniforms.uStemTip.value.setHSL(hsl.h, STEM_SATURATION, STEM_LIGHT_TIP);

    this._scratchColor.set(vivid).getHSL(hsl);
    this._stemUniforms.uEarColor.value.setHSL(
      hsl.h, hsl.s * (1 - EAR_DESATURATE), EAR_LIGHT);

    // The far row is mixed toward this, so it has to be the background the row
    // actually sits against.
    this._backUniforms.uTop.value.copy(BACK_TOP);
    this._backUniforms.uBottom.value.setHSL(hsl.h, 0.12, 0.075);
    this._stemUniforms.uBackColor.value.copy(this._backUniforms.uBottom.value);
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    const canvasPixelHeight = window.innerHeight * Math.min(window.devicePixelRatio || 1, 2);
    const fovRadians = THREE.MathUtils.degToRad(this._camera.fov);
    this._stemUniforms.uPixelScale.value =
      canvasPixelHeight / (2 * Math.tan(fovRadians / 2));

    const palette = profile.artworkPalette;
    if (palette !== this._lastPaletteRef) {
      this._lastPaletteRef = palette;
      this._applyPalette(palette);
    }

    // Pause freezes the angles, the velocities AND the gusts in place: the
    // accumulator is simply not fed, so no substep runs at all.
    if (paused) return;

    this._time += deltaTime;

    const director = profile.director;
    const bands = (director && director.bands) || null;
    const live = !!(director && director.hasReliableGrid && bands);
    const character = (director && director.character) || null;
    const dynamics = character && character.dynamics != null ? character.dynamics : 0.5;
    this._windScale = lerp(DYNAMICS_MIN, DYNAMICS_MAX, clamp01(dynamics));

    const progress = profile.duration > 0 ? clamp01(profile.progress) : 0.5;
    const lfo = Math.sin((2 * Math.PI * this._time) / BACKGROUND_WIND_PERIOD_S);
    const seasonal = lerp(BACKGROUND_WIND_PROGRESS_MIN, 1.0, Math.sin(Math.PI * progress));
    this._backgroundWind = this._forcedWind != null
      ? this._forcedWind
      : lfo * BACKGROUND_WIND_FORCE * seasonal * this._windScale;

    const low = live ? bands.low : 0.5;
    const mid = live ? bands.mid : 0.5;
    const high = live ? bands.high : 0.5;

    if (live) {
      // A gust is born on a RISE of the mid band, never on a level - the same
      // rule sumi.js and resonance.js use for their own excitation.
      const rise = mid - this._prevMid;
      this._prevMid = mid;
      if (rise >= GUST_RISE_THRESHOLD && this._time - this._lastGustAt >= GUST_MIN_SPACING_S) {
        this._spawnGust(rise * 6.0, low, high);
      }
    } else if (this._time >= this._nextFallbackGust) {
      // Without an analysis the wind simply keeps its own time. This file
      // barely depends on the analysis, which is exactly what makes it safe on
      // the repertoire it is for.
      this._spawnGust(0.35 + Math.random() * 0.5, 0.5, 0.5);
      this._nextFallbackGust = this._time
        + lerp(FALLBACK_GUST_MIN_S, FALLBACK_GUST_MAX_S, Math.random());
    }
    if (this._forceGust && this._time - this._lastGustAt > 1.5) {
      this._spawnGust(0.8, low, high);
    }

    // FIXED STEP. A variable dt would make the return to rest - and the speed a
    // gust crosses at - depend on the framerate.
    this._accumulator += deltaTime;
    let steps = 0;
    while (this._accumulator >= FIXED_DT && steps < MAX_SUBSTEPS) {
      this._step(FIXED_DT);
      this._accumulator -= FIXED_DT;
      steps++;
    }
    if (steps === MAX_SUBSTEPS) this._accumulator = 0;

    for (let i = 0; i < this._stemCount; i++) this._angleUniform[i] = this._angle[i];
  }

  dispose() {
    this._scene.remove(this._stemMesh);
    this._scene.remove(this._earMesh);
    this._scene.remove(this._back);
    this._stemGeometry.dispose();
    this._stemMaterial.dispose();
    this._earGeometry.dispose();
    this._earMaterial.dispose();
    this._backGeometry.dispose();
    this._backMaterial.dispose();
  }
}
